import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import {
  AbsoluteWhiteColors,
  AbsoluteBlackColors,
} from '@picsart/design-system/foundation/colors';
import { Effects } from '@picsart/design-system/foundation/effects';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canTouch } from '@picsart/design-system/styleUtils/hoverTouch';

// Card-internal stacking order. DS `StackingContext` tokens are app-level
// (>=1000); these small layer indices stay local to the Card subtree.
// Implicit bottom layers (CardMedia owns these): base media (1), video
// icon (3). Overlay layers, bottom → top:
//   TITLE          (3) — title at bottom; hidden when info overlay shows
//   HOVER_OVERLAY  (4) — dim background on hover
//   INFO_OVERLAY   (5) — title + description on hover (top of card subtree)
const CARD_Z_TITLE = 3;
const CARD_Z_HOVER_OVERLAY = 4;
const CARD_Z_INFO_OVERLAY = 5;

const useStyles = createUseStyles(
  {
    titleOverlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: [Spacings.s12, Spacings.s12],
      background: `linear-gradient(to top, ${Effects.alpha80} 0%, transparent 100%)`,
      color: AbsoluteWhiteColors.base.default,
      fontSize: FontSizes.t4,
      fontWeight: FontWeights.semibold,
      pointerEvents: 'none',
      // Prevent text selection
      userSelect: 'none',
      WebkitUserSelect: 'none',
      transition: `opacity 0.2s ${Easings.easeOut}`,
      zIndex: CARD_Z_TITLE,
      // Smaller on mobile
      [`@media (max-width: ${Breakpoints.xs - 1}px)`]: {
        padding: [Spacings.s8, Spacings.s8],
        fontSize: FontSizes.t3,
      },
      // Always show on touch devices (info overlay never appears there).
      [canTouch]: {
        opacity: '1 !important',
      },
    },

    byline: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s4,
      marginTop: Spacings.s4,
      fontSize: FontSizes.t2,
      fontWeight: FontWeights.normal,
      color: AbsoluteWhiteColors.base.default,
      opacity: 0.85,
      minWidth: 0,
      paddingRight: Spacings.s32,
    },
    bylineName: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Applied when the parent Card is hovered (driven by Card.tsx React state)
    // — hides the title so infoOverlay can take over the top area.
    titleOverlayHidden: {
      opacity: 0,
    },

    titleOverlayOverVideo: {
      [canTouch]: {
        transform: 'translateZ(0)',
      },
    },
    // 1px overhang covers compositor drift from the parent's hover scale.
    infoOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      padding: Spacings.s12,
      background: `linear-gradient(to bottom, ${Effects.alpha80} 0%, ${Effects.alpha50} 70%, transparent 100%)`,
      color: AbsoluteWhiteColors.base.default,
      opacity: 0,
      transition: `opacity 0.25s ${Easings.easeOut}, transform 0.25s ${Easings.easeOut}`,
      pointerEvents: 'none',
      // Prevent text selection
      userSelect: 'none',
      WebkitUserSelect: 'none',
      zIndex: CARD_Z_INFO_OVERLAY,
      // Hide on touch devices
      [canTouch]: {
        display: 'none',
      },
    },
    // Applied when the parent Card is hovered.
    infoOverlayVisible: {
      opacity: 1,
    },
    infoTitle: {
      fontSize: FontSizes.t4,
      fontWeight: FontWeights.semibold,
      marginBottom: Spacings.s4,
      lineHeight: 1,
    },
    infoDescription: {
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.medium,
      opacity: 0.9,
      lineHeight: 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    infoHint: {
      fontSize: FontSizes.t2,
      opacity: 0.7,
      marginTop: Spacings.s8,
      fontStyle: 'italic',
    },
    hoverOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: Effects.alpha50,
      opacity: 0,
      transition: `opacity 0.25s ${Easings.easeOut}`,
      pointerEvents: 'none',
      zIndex: CARD_Z_HOVER_OVERLAY,
      // Hide on touch devices
      [canTouch]: {
        display: 'none',
      },
    },
    // Applied when the parent Card is hovered.
    hoverOverlayVisible: {
      opacity: 1,
    },
    // Wrapper around the DS Button — owns positioning + opacity fade.
    // The DS Button itself owns all chrome (background, border, hover, active).
    tryNowButton: {
      pointerEvents: 'auto',
      opacity: 0,
      transition: `opacity 0.25s ${Easings.easeOut}`,
    },
    // Applied when the parent Card is hovered.
    tryNowButtonVisible: {
      opacity: 1,
    },
    tryNowAction: {
      background: AbsoluteWhiteColors.base.default,
      color: AbsoluteBlackColors.base.default,
    },
  },
  { name: 'card-overlays' }
);

export default useStyles;
